import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {ProxyRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/ProxyRequest';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {XmlParserUtil} from '@mintdata-wsp/common/controllers/spreadsheet/functions/util/xml/XmlParserUtil';
import {IFunctionContainer} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {Function, Param} from '@mintdata/mint-kit/api/decorators/Function';
import {SheetType} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {ABinaryData} from '@mintdata/mint-kit/api/values/ABinaryData';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {BaseValue} from '@mintdata/mint-kit/api/values/BaseValue';
import {EvalErrors} from '@mintdata/mint-kit/api/values/errors/EvalErrors';
import {AFrame} from '@mintdata/mint-kit/api/values/frames/AFrame';
import {FrameField} from '@mintdata/mint-kit/api/values/frames/FrameField';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {JsonUtil} from '@mintdata/mint-kit/impl/util/JsonUtil';
import bind from 'bind-decorator';

type FetchFn = (method:string, url:string, body:BodyInit, headers:any) => Promise<Response>
const RESPONSE_FORMATS = ['json', 'text', 'xml', 'binary'];

export class ApiFunctions implements IFunctionContainer
{
    public get namespace()
    {
        return 'api';
    }

    @Function({
        title: 'GET_API(appCredentialsName, authProviderName, urlAddress)',
        tip: 'Fetching the contents of a URL',
        alias: 'GET_API',
        example: 'GET_API("foo", "okta", "users")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with URL contents',
                tip: 'Returns the parsed URL contents as a frame'
            }
    })
    @bind
    public getApi(@Param({
                      title: 'appCredentialsName',
                      tip: 'Application credentials settings name.',
                      type: Type.String
                  }) appCredentialsName:AString,
                  @Param({
                      title: 'authProviderName',
                      tip: 'Authentication provider name.',
                      type: Type.String
                  }) authProviderName:AString,
                  @Param({
                      title: 'url',
                      tip: 'The URL to fetch',
                      type: Type.String
                  }) url:AString,
                  @Param({
                      title: 'httpHeaders',
                      tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                      type: Type.Frame,
                      optional: true
                  }) headers:AFrame,
                  @Param({
                      title: 'includeHttpDetails',
                      tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                      type: Type.Boolean,
                      optional: true
                  }) includeHttpDetails:ABoolean,
                  @Param({
                      title: 'responseFormat',
                      tip: 'Response format',
                      type: Type.String,
                      optional: true
                  }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._proxyApiRequest(
            appCredentialsName,
            authProviderName,
            'GET',
            url,
            null,
            headers,
            includeHttpDetails,
            responseFormat
        );
    }

    @Function({
        title: 'POST_API(appCredentialsName, authProviderName, urlAddress)',
        tip: 'Fetching the contents of a URL',
        alias: 'POST_API',
        example: 'POST_API("foo", "okta", "users")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with URL contents',
                tip: 'Returns the parsed URL contents as a frame'
            }
    })
    @bind
    public postApi(@Param({
                       title: 'appCredentialsName',
                       tip: 'Application credentials settings name.',
                       type: Type.String
                   }) appCredentialsName:AString,
                   @Param({
                       title: 'authProviderName',
                       tip: 'Authentication provider name.',
                       type: Type.String
                   }) authProviderName:AString,
                   @Param({
                       title: 'url',
                       tip: 'The URL to POST to',
                       type: Type.String
                   }) url:AString,
                   @Param({
                       title: 'body',
                       tip: 'The body to use in POST',
                       type: Type.String,
                       optional: true
                   }) body:AString,
                   @Param({
                       title: 'httpHeaders',
                       tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                       type: Type.Frame,
                       optional: true
                   }) headers:AFrame,
                   @Param({
                       title: 'includeHttpDetails',
                       tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                       type: Type.Boolean,
                       optional: true
                   }) includeHttpDetails:ABoolean,
                   @Param({
                       title: 'responseFormat',
                       tip: 'Response format',
                       type: Type.String,
                       optional: true
                   }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._proxyApiRequest(
            appCredentialsName,
            authProviderName,
            'POST',
            url,
            body.value,
            headers,
            includeHttpDetails,
            responseFormat
        );
    }

    @Function({
        title: 'PUT_API(appCredentialsName, authProviderName, urlAddress)',
        tip: 'Fetching the contents of a URL',
        alias: 'PUT_API',
        example: 'PUT_API("foo", "okta", "users")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with URL contents',
                tip: 'Returns the parsed URL contents as a frame'
            }
    })
    @bind
    public putExt(@Param({
                      title: 'appCredentialsName',
                      tip: 'Application credentials settings name.',
                      type: Type.String
                  }) appCredentialsName:AString,
                  @Param({
                      title: 'authProviderName',
                      tip: 'Authentication provider name.',
                      type: Type.String
                  }) authProviderName:AString,
                  @Param({
                      title: 'url',
                      tip: 'The URL to PUT to',
                      type: Type.String
                  }) url:AString,
                  @Param({
                      title: 'body',
                      tip: 'The body to use in PUT',
                      type: Type.String,
                      optional: true
                  }) body:AString,
                  @Param({
                      title: 'httpHeaders',
                      tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                      type: Type.Frame,
                      optional: true
                  }) headers:AFrame,
                  @Param({
                      title: 'includeHttpDetails',
                      tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                      type: Type.Boolean,
                      optional: true
                  }) includeHttpDetails:ABoolean,
                  @Param({
                      title: 'responseFormat',
                      tip: 'Response format',
                      type: Type.String,
                      optional: true
                  }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._proxyApiRequest(
            appCredentialsName,
            authProviderName,
            'PUT',
            url,
            body.value,
            headers,
            includeHttpDetails,
            responseFormat
        );
    }

    @Function({
        title: 'PATCH_API(appCredentialsName, authProviderName, urlAddress)',
        tip: 'Fetching the contents of a URL',
        alias: 'PATCH_API',
        example: 'PATCH_API("foo", "okta", "users")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with URL contents',
                tip: 'Returns the parsed URL contents as a frame'
            }
    })
    @bind
    public patchExt(@Param({
                        title: 'appCredentialsName',
                        tip: 'Application credentials settings name.',
                        type: Type.String
                    }) appCredentialsName:AString,
                    @Param({
                        title: 'authProviderName',
                        tip: 'Authentication provider name.',
                        type: Type.String
                    }) authProviderName:AString,
                    @Param({
                        title: 'url',
                        tip: 'The URL to PATCH to',
                        type: Type.String
                    }) url:AString,
                    @Param({
                        title: 'body',
                        tip: 'The body to use in PATCH',
                        type: Type.String,
                        optional: true
                    }) body:AString,
                    @Param({
                        title: 'httpHeaders',
                        tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                        type: Type.Frame,
                        optional: true
                    }) headers:AFrame,
                    @Param({
                        title: 'includeHttpDetails',
                        tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                        type: Type.Boolean,
                        optional: true
                    }) includeHttpDetails:ABoolean,
                    @Param({
                        title: 'responseFormat',
                        tip: 'Response format',
                        type: Type.String,
                        optional: true
                    }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._proxyApiRequest(
            appCredentialsName,
            authProviderName,
            'PATCH',
            url,
            body.value,
            headers,
            includeHttpDetails,
            responseFormat
        );
    }

    @Function({
        title: 'DELETE_API(appCredentialsName, authProviderName, urlAddress)',
        tip: 'DELETE request',
        alias: 'DELETE_API',
        example: 'DELETE_API("foo", "okta", "users")',
        icon: '',
        returns:
            {
                type: SheetType.AnyIValue,
                title: 'Frame with response contents',
                tip: 'Frame with response contents'
            }
    })
    @bind
    public deleteExt(@Param({
                         title: 'appCredentialsName',
                         tip: 'Application credentials settings name.',
                         type: Type.String
                     }) appCredentialsName:AString,
                     @Param({
                         title: 'authProviderName',
                         tip: 'Authentication provider name.',
                         type: Type.String
                     }) authProviderName:AString,
                     @Param({
                         title: 'url',
                         tip: 'Request URL',
                         type: Type.String
                     }) url:AString,
                     @Param({
                         title: 'httpHeaders',
                         tip: 'A frame with HTTP headers to send (first/second column are used for HTTP header name and value)',
                         type: Type.Frame,
                         optional: true
                     }) headers:AFrame,
                     @Param({
                         title: 'includeHttpDetails',
                         tip: 'If true, result frame contains not only data, but also response info. Default is FALSE',
                         type: Type.Boolean,
                         optional: true
                     }) includeHttpDetails:ABoolean,
                     @Param({
                         title: 'responseFormat',
                         tip: 'Response format',
                         type: Type.String,
                         optional: true
                     }) responseFormat:AString):Promise<IValue | AError>
    {
        return this._proxyApiRequest(
            appCredentialsName,
            authProviderName,
            'DELETE',
            url,
            null,
            headers,
            includeHttpDetails,
            responseFormat
        );
    }

    private _proxyApiRequest(appCredentialsName:AString,
                             authProviderName:AString,
                             method:string,
                             vUrl:AString,
                             body:string,
                             vHeaders:AFrame,
                             vIncludeHttpDetails:ABoolean,
                             vResponseFormat:AString = null):Promise<IValue | AError>
    {
        const fetchFn = (method:string, url:string, body:BodyInit, headers:any):Promise<Response> =>
            API.proxy.proxyApiRequest(
                authProviderName.value,
                commonControllers.context.currentOrganizationSid(),
                appCredentialsName.value,
                new ProxyRequest(method, url, body as string, headers)
            ) as any;

        return this._doFetch(
            method,
            vUrl,
            vHeaders,
            vIncludeHttpDetails,
            vResponseFormat,
            body,
            fetchFn
        );
    }

    private async _doFetch(method:string, vUrl:AString, vHeaders:AFrame, vIncludeHttpDetails:ABoolean, vResponseFormat:AString, body:BodyInit, fetchFn:FetchFn):Promise<IValue | AError>
    {
        const url = vUrl.value;
        const requiresContentType = method === 'POST' || method === 'PUT' || method === 'PATCH';
        const headers = this._computeHeaders(vHeaders, requiresContentType, body);
        const includeHttpDetails = vIncludeHttpDetails && vIncludeHttpDetails.value || false;
        const responseFormat = vResponseFormat && vResponseFormat.value || 'json';

        if( RESPONSE_FORMATS.indexOf(responseFormat) < 0 )
            return new AError('#INVALID_RESPONSE_FORMAT');

        return fetchFn(method, url, body, headers)
            .then((response) =>
                this._handleResponse(url, response, responseFormat, includeHttpDetails)
            )
            .catch((err) =>
            {
                const errorMessage = err && err.message || err;
                return Frames.forError(`Error: "${errorMessage}", when making http request on URL: ${url}: `);
            });
    }

    private _computeHeaders(headersFrame:AFrame, requiresContentType:boolean, body:BodyInit):any
    {
        const headers = {};

        if( headersFrame != null && !BaseValue.isValueEmpty(headersFrame) &&
            headersFrame.getType() === Type.Frame )
        {
            const records = headersFrame.getRecords();

            for( let record of records )
            {
                const cells = record.getCells();

                if( cells && cells.length && cells.length >= 2 )
                    headers[cells[0].toString()] = cells[1].toString();
            }
        }

        if( requiresContentType )
        {
            const hasContentType = Object.keys(headers).some(headerName =>
                headerName && headerName.toLocaleLowerCase() === 'content-type'
            );

            // we shouldn't set Content-Type for FormData
            // see: https://muffinman.io/uploading-files-using-fetch-multipart-form-data/
            if( !hasContentType && !(body instanceof FormData) )
            {
                headers['Content-Type'] = 'application/json';
            }
        }

        return headers;
    }

    private async _handleResponse(fullURL:string, response:Response, responseFormat:string, includeHttpDetails = false):Promise<IValue | AError>
    {
        if( !response )
            return EvalErrors.Data;

        const responseStatus = response.status;
        const responseHeaders = {};
        response.headers.forEach((value, key) =>
            responseHeaders[key] = value
        );

        if( responseStatus >= 400 && responseStatus <= 499 || responseStatus >= 500 )
        {
            const bodyText = await response.text();
            return JsonUtil.convertJsonObjectToFrame({
                http_status: responseStatus,
                http_headers: responseHeaders,
                http_body: bodyText,
                error: `Server responded with an HTTP status of ${responseStatus} for URL: ${fullURL}`
            });
        }

        let dataValue = null;
        let dataType = null;
        switch( responseFormat )
        {
            case 'json':
            {
                dataValue = JsonUtil.convertJsonObjectToFrame(
                    await response.json()
                );
                dataType = Type.Frame;
                break;
            }
            case 'xml':
            {
                const json = XmlParserUtil.parseXml(
                    await response.text()
                );
                dataValue = JsonUtil.convertJsonObjectToFrame(json);
                dataType = Type.Frame;
                break;
            }
            case 'text':
            {
                dataValue = new AString(
                    await response.text()
                );
                dataType = Type.String;
                break;
            }
            default:
                dataValue = new ABinaryData(new Uint8Array(
                    await response.arrayBuffer()
                ));
                dataType = Type.BinaryData;
                break;
        }

        if( !includeHttpDetails )
            return dataValue;

        const fields = [
            new FrameField('data', dataType),
            new FrameField('http_status', Type.Integer),
            new FrameField('http_headers', Type.Frame)
        ];

        const data = [[
            dataValue,
            new AInteger(responseStatus),
            JsonUtil.convertJsonObjectToFrame(responseHeaders)
        ]];

        return Frames.fromFieldsAndData(fields, data);
    }
}
